import { Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { getLocalDocumentUrlFromS3 } from '@/api/document';

import { Loader } from '@/components/Icon/Loader';

import { SourceType } from '@/types/document';

import { isImageContentType, isPDFContentType } from '@/utils/string';

import { FileThumbnail } from './FileThumbnail';

import { ImageThumbnailContainer } from './File.styled';

type DocumentThumbnailFromUriProps = {
  uri: string;
  contentType?: string;
  onClick?: (uri: string, contentType?: string) => void;
  error?: string | null;
};

export function DocumentThumbnailFromUri({
  uri,
  contentType,
  onClick,
  error,
}: Readonly<DocumentThumbnailFromUriProps>) {
  const getSrcType = () => {
    if (contentType && isPDFContentType(contentType)) {
      return SourceType.PDF;
    }
    if (contentType && isImageContentType(contentType)) {
      return SourceType.Image;
    }
    return SourceType.Image;
  };
  const srcType = getSrcType();

  const { data: url, isLoading } = useQuery({
    queryKey: ['document-thumbnail', uri],
    queryFn: () => getLocalDocumentUrlFromS3(uri),
    enabled: !!uri,
  });

  const handleClick = () => {
    onClick?.(uri, contentType);
  };

  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      sx={{
        display: 'block',
        width: '100%',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        background: 'transparent',
        textAlign: 'left',
      }}
    >
      {isLoading || !url ? (
        <ImageThumbnailContainer
          sx={{
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loader />
        </ImageThumbnailContainer>
      ) : (
        <FileThumbnail src={url} type={srcType} />
      )}
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Box>
  );
}
